import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell,
} from "recharts";
import { Sparkles, Check, X, Loader2, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api, formatApiError } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";

const ease = [0.16, 1, 0.3, 1];
const PRIO_CLS = { high: "bg-gold/15 text-gold", medium: "bg-violet/10 text-violet", low: "bg-navy/10 text-navy/50" };
const STATUS_CLS = {
  optimal: "bg-violet/10 text-violet",
  friction: "bg-gold/15 text-gold",
  dead: "bg-navy/10 text-navy/50",
};
const PIE_COLORS = ["#5B21B6", "#0A1428", "#C9A24B"];

export default function Intelligence() {
  const { t } = useI18n();
  const [d, setD] = useState(null);
  const [recs, setRecs] = useState([]);
  const [learning, setLearning] = useState(null);
  const [generating, setGenerating] = useState(false);

  const loadRecs = useCallback(() => {
    api.get("/intelligence/recommendations").then((r) => setRecs(r.data)).catch(() => {});
    api.get("/intelligence/learning").then((r) => setLearning(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/intelligence/overview").then((r) => setD(r.data)).catch(() => {});
    loadRecs();
  }, [loadRecs]);

  const generateRecs = async () => {
    setGenerating(true);
    try { await api.post("/intelligence/recommendations/generate"); toast.success("✨ OMNIA AI"); loadRecs(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
    finally { setGenerating(false); }
  };
  const setStatus = async (id, status) => {
    setRecs((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
    try { await api.patch(`/intelligence/recommendations/${id}`, { status }); loadRecs(); } catch (e) { toast.error("Error"); loadRecs(); }
  };

  return (
    <OSLayout title={t("os.titles.intelligence")}>
      {!d ? (
        <div className="text-navy/40">{t("os.loading")}</div>
      ) : (
        <div className="space-y-8">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
            <div className="overline text-navy/40 mb-2">{t("os.intel.overline")}</div>
            <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">{t("os.intel.h")}</h2>
          </motion.div>

          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
            {d.kpis.map((k, i) => (
              <motion.div key={k.key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease, delay: i * 0.05 }}
                data-testid={`intel-kpi-${k.key}`}
                className="bg-white border border-navy/10 rounded-lg p-5">
                <div className="overline text-navy/40 text-[0.6rem]">{t(`os.kpi.${k.key}`)}</div>
                <div className="mt-2 text-2xl font-medium tracking-tight">{k.value}</div>
                <div className={`mt-1 text-xs ${k.trend === "up" ? "text-violet" : "text-gold"}`}>{k.delta}</div>
              </motion.div>
            ))}
          </div>

          {/* AI Recommendations (continuous learning) */}
          <div data-testid="intel-recs">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-4">
              <div>
                <div className="overline text-violet mb-1 inline-flex items-center gap-1.5"><Lightbulb className="w-3.5 h-3.5" /> {t("os.recs.title")}</div>
                <p className="text-sm text-navy/50">{t("os.recs.subtitle")}</p>
              </div>
              <div className="flex items-center gap-4">
                {learning && learning.total_generated > 0 && (
                  <div className="hidden sm:flex items-center gap-4 text-xs text-navy/50">
                    <span data-testid="intel-recs-adoption"><span className="text-violet font-medium">{learning.adoption_rate}%</span> {t("os.recs.adoption")}</span>
                    <span>{learning.applied}/{learning.total_generated} {t("os.recs.appliedCount")}</span>
                  </div>
                )}
                <button data-testid="intel-generate-recs" onClick={generateRecs} disabled={generating}
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white text-sm px-5 py-2.5 hover:opacity-90 transition-opacity disabled:opacity-60">
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {generating ? t("os.recs.generating") : t("os.recs.generate")}
                </button>
              </div>
            </div>
            {recs.length === 0 ? (
              <div className="text-navy/40 text-sm border border-dashed border-navy/15 rounded-lg py-12 text-center">{t("os.recs.empty")}</div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {recs.map((r, i) => (
                  <motion.div key={r.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease, delay: i * 0.04 }}
                    data-testid={`intel-rec-${r.id}`}
                    className={`bg-white border rounded-lg p-5 ${r.status === "open" ? "border-navy/10" : "border-navy/5 opacity-60"}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${PRIO_CLS[r.priority] || PRIO_CLS.medium}`}>{t(`os.recs.priority.${r.priority}`) || r.priority}</span>
                        <span className="overline text-navy/30 text-[0.55rem]">{r.module}</span>
                      </div>
                      {r.status !== "open" && <span className="text-[10px] text-navy/40">{t(`os.recs.${r.status}`)}</span>}
                    </div>
                    <h3 className="font-medium mt-2">{r.title}</h3>
                    <p className="text-sm text-navy/50 mt-1">{r.detail}</p>
                    {r.status === "open" && (
                      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-navy/5">
                        <button data-testid={`intel-rec-apply-${r.id}`} onClick={() => setStatus(r.id, "applied")}
                          className="inline-flex items-center gap-1.5 text-xs rounded-full bg-violet/10 text-violet px-3 py-1.5 hover:bg-violet/20 transition-colors"><Check className="w-3.5 h-3.5" /> {t("os.recs.apply")}</button>
                        <button data-testid={`intel-rec-dismiss-${r.id}`} onClick={() => setStatus(r.id, "dismissed")}
                          className="inline-flex items-center gap-1.5 text-xs rounded-full border border-navy/10 text-navy/50 px-3 py-1.5 hover:border-navy/30 transition-colors"><X className="w-3.5 h-3.5" /> {t("os.recs.dismiss")}</button>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            {/* Flow chart */}
            <div className="lg:col-span-2 bg-white border border-navy/10 rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="overline text-navy/40">{t("os.intel.flowTitle")}</div>
                <div className="flex gap-4 text-xs">
                  <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-violet" />{t("os.intel.flow")}</span>
                  <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-gold" />{t("os.intel.conversion")}</span>
                </div>
              </div>
              {(!d.flow || d.flow.length === 0) ? (
                <div data-testid="intel-flow-empty" className="h-[260px] flex items-center justify-center text-navy/40 text-sm border border-dashed border-navy/15 rounded-lg">{t("os.intel.noData")}</div>
              ) : (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={d.flow} margin={{ left: -20, right: 8 }}>
                  <defs>
                    <linearGradient id="gFlow" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6D28D9" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#6D28D9" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="month" stroke="#0A142855" tickLine={false} axisLine={false} fontSize={12} />
                  <YAxis stroke="#0A142855" tickLine={false} axisLine={false} fontSize={12} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid rgba(10,20,40,0.1)", fontSize: 12 }} />
                  <Area type="monotone" dataKey="flow" stroke="#6D28D9" strokeWidth={2} fill="url(#gFlow)" />
                  <Area type="monotone" dataKey="conversion" stroke="#C9A24B" strokeWidth={2} fill="transparent" />
                </AreaChart>
              </ResponsiveContainer>
              )}
            </div>

            {/* Revenue split */}
            <div className="bg-white border border-navy/10 rounded-lg p-6">
              <div className="overline text-navy/40 mb-2">{t("os.intel.revenueTitle")}</div>
              {(!d.revenue_split || d.revenue_split.length === 0) ? (
                <div data-testid="intel-revenue-empty" className="h-[230px] flex items-center justify-center text-navy/40 text-sm border border-dashed border-navy/15 rounded-lg">{t("os.intel.noData")}</div>
              ) : (
              <>
              <ResponsiveContainer width="100%" height={230}>
                <PieChart>
                  <Pie data={d.revenue_split} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3}>
                    {d.revenue_split.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid rgba(10,20,40,0.1)", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-4 text-xs mt-2">
                {d.revenue_split.map((e, i) => (
                  <span key={e.name} className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />{e.name} {e.value}%
                  </span>
                ))}
              </div>
              </>
              )}
            </div>
          </div>

          {/* Zones */}
          {d.zones && d.zones.length > 0 && (
          <div>
            <div className="overline text-navy/40 mb-4">{t("os.intel.zonesTitle")}</div>
            <div className="grid md:grid-cols-2 gap-4">
              {d.zones.map((z, i) => {
                return (
                  <motion.div key={z.zone} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease, delay: i * 0.06 }}
                    data-testid={`intel-zone-${i}`}
                    className="bg-white border border-navy/10 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-medium">{z.zone}</h3>
                      <span className={`text-[10px] px-2 py-1 rounded-full ${STATUS_CLS[z.status]}`}>{t(`os.zoneStatus.${z.status}`)}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div><div className="text-xl font-medium">{z.flow}</div><div className="overline text-navy/40 text-[0.6rem]">{t("os.intel.dailyFlow")}</div></div>
                      <div><div className="text-xl font-medium">{z.dwell}</div><div className="overline text-navy/40 text-[0.6rem]">{t("os.intel.dwell")}</div></div>
                      <div><div className="text-xl font-medium">{z.nps}</div><div className="overline text-navy/40 text-[0.6rem]">{t("os.intel.npsZonal")}</div></div>
                    </div>
                    <p className="text-sm text-navy/50 leading-snug border-t border-navy/5 pt-3">{z.note}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
          )}
        </div>
      )}
    </OSLayout>
  );
}
